import React from 'react'
import { Field } from 'redux-form'
import { pt, buildPropTypes } from 'common-data/proptypehelpers'
import { FieldLayoutBox } from './FieldLayoutBox'
import { LookupValueFormatter, FieldValueFormatter, FieldValueParser } from './FieldValueConverters'
import { ah, ReduxWrapper, compose } from 'common-data/datahelpers'
import { Input } from 'reactstrap'
import _ from 'common-tools/LodashHelper'
import styleClasses from './ComboBox.less'
import { FAIcon } from './FAIcon'

class ComboBoxClassPlain extends React.Component {
  constructor (props) {
    super(props)

    this.containerRef = React.createRef()
    this.optionRefs = {}
    this.typeBuffer = ''
    this.typeTimer = null

    this.state = {
      isOpen: false,
      autosetComplete: false,
      optionsAvailable: false,
      firstDidUpdate: true,
      highlightedIndex: -1
    }
  }

  static propTypes = buildPropTypes([

  ], {
    options: pt.array,
    valueProp: pt.string,
    textProp: pt.string,
    noOptionsText: pt.string,
    extraDataMap: pt.oneOfType([
      pt.string,
      pt.arrayOf(pt.shape({
        Source: pt.string,
        Dest: pt.string
      })),
      pt.func
    ]),
    autosetFirstOption: pt.bool
  })

  static defaultProps = {
    valueProp: 'Value',
    textProp: 'Text',
    options: [],
    noOptionsText: 'loading...',
    autosetFirstOption: false
  }

  componentDidMount () {
    const { options = [] } = this.props
    if (options.length > 0) {
      this.setState({
        optionsAvailable: true
      })
    }
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClickOutside)
    if (this.typeTimer) clearTimeout(this.typeTimer)
  }

  handleClickOutside = (event) => {
    if (this.containerRef.current && !this.containerRef.current.contains(event.target)) {
      this.closeDropdown()
    }
  }

  componentDidUpdate ({ filterValue: prevFilterValue, options: prevOptions = [] },
    { optionsAvailable: prevOptionsAvailable }) {

    const {
      options: currOptions = [],
      filterValue: currFilterValue,
      formSetFieldValue,
      meta,
      input,
      extraDataMap,
      textProp,
      valueProp,
      autosetFirstOption
    } = this.props

    const {
      optionsAvailable: currOptionsAvailable,
      firstDidUpdate
    } = this.state

    const filtersSame = _.isEqual(prevFilterValue, currFilterValue)

    if (filtersSame === false) {
      if (!firstDidUpdate) {
        formSetFieldValue(meta.form, input.name, (_.first(_.filter(currOptions, currFilterValue)) || {})[valueProp])
      } else {
        this.setState({
          firstDidUpdate: false
        })
      }
    }

    if (prevOptionsAvailable === false && currOptionsAvailable === true || !_.isEqual(prevOptions, currOptions)) {
      if (autosetFirstOption === true) {
        const isCurrentValueInvalid = input.value !== undefined && input.value !== '' && !_.find(currOptions, o => o[valueProp] == input.value);
        if (!meta.initial || isCurrentValueInvalid) {
          let firstOption = currOptions[0] || {}
          if (firstOption[valueProp] !== undefined) {
            input.onChange(firstOption[valueProp])

            if (extraDataMap) {
              let extraData = firstOption.ExtraData || {}
              if (_.isString(extraDataMap)) {
                formSetFieldValue(meta.form, extraDataMap, firstOption[textProp])
              } else if (_.isArray(extraDataMap)) {
                for (var mapInfo of extraDataMap) {
                  if (_.has(extraData, mapInfo.Source)) {
                    formSetFieldValue(meta.form, mapInfo.Dest, extraData[mapInfo.Source])
                  }
                }
              } else if (_.isFunction(extraDataMap)) {
                extraDataMap(firstOption, extraData, (field, value) => {
                  formSetFieldValue(meta.form, field, value, 'set')
                })
              } else {
                throw Error('Invalid extraDataMap, must be a string, array, or function!')
              }
            }
          }
        }
        this.setState({
          autosetComplete: true
        })
      }
    }

    if (prevOptions.length === 0 && currOptions.length > 0) {
      if (currOptionsAvailable === false) {
        this.setState({
          optionsAvailable: true
        })
      }
    }
  }

  onChange (selectedVal) {
    const { input: { onChange }, formSetFieldValue, meta: { form }, extraDataMap, options, textProp, valueProp } = this.props
    const value = (selectedVal && selectedVal.target) ? selectedVal.target.value : selectedVal

    onChange(value)

    if (extraDataMap) {
      let foundOption = _.find(options, (o) => { return o[valueProp] == value })

      if (foundOption) {
        let extraData = foundOption.ExtraData || {}
        if (_.isString(extraDataMap)) {
          formSetFieldValue(form, extraDataMap, foundOption[textProp])
        } else if (_.isArray(extraDataMap)) {
          for (var mapInfo of extraDataMap) {
            if (_.has(extraData, mapInfo.Source)) {
              formSetFieldValue(form, mapInfo.Dest, extraData[mapInfo.Source])
            }
          }
        } else if (_.isFunction(extraDataMap)) {
          extraDataMap(foundOption, extraData, (field, value) => {
            formSetFieldValue(form, field, value, 'set')
          })
        } else {
          throw Error('Invalid extraDataMap, must be a string, array, or function!')
        }
      }
    }
  }

  toggleDropdown = (list = [], currentValue) => {
    if (this.props.readonly) return
    if (this.state.isOpen) {
      this.closeDropdown()
    } else {
      this.setState({ isOpen: true, highlightedIndex: this.findIndexByValue(list, currentValue) },
        () => this.scrollHighlightedIntoView())
    }
  }

  closeDropdown = () => {
    this.clearTypeBuffer()
    this.setState({ isOpen: false, highlightedIndex: -1 })
  }

  handleOptionClick = (val) => {
    this.onChange(val)
    this.closeDropdown()
  }

  clearTypeBuffer = () => {
    if (this.typeTimer) clearTimeout(this.typeTimer)
    this.typeTimer = null
    this.typeBuffer = ''
  }

  getOptionText (option) {
    const { valueProp = 'Value', textProp = 'Text' } = this.props
    const text = option[textProp] !== undefined ? option[textProp] : option[valueProp]
    return _.isNil(text) ? '' : String(text)
  }

  findIndexByValue (list, currentValue) {
    const { valueProp = 'Value' } = this.props
    return _.findIndex(list, o => o[valueProp] == currentValue) // eslint-disable-line eqeqeq
  }

  scrollHighlightedIntoView () {
    const el = this.optionRefs[this.state.highlightedIndex]
    if (el && _.isFunction(el.scrollIntoView)) {
      el.scrollIntoView({ block: 'nearest' })
    }
  }

  // Finds the option matching the current type-ahead buffer, wrapping around the list.
  // A buffer of a single repeated character ('l', 'll', ...) cycles through every option
  // starting with that character, which is how a native <select> behaves.
  findTypeAheadIndex (list, startIndex) {
    const buffer = this.typeBuffer
    if (buffer.length === 0 || list.length === 0) return -1

    const repeatedChar = buffer.split('').every(c => c === buffer[0])
    const needle = repeatedChar ? buffer[0] : buffer
    const begin = repeatedChar ? startIndex + 1 : startIndex

    const search = (matches) => {
      for (let offset = 0; offset < list.length; offset++) {
        const index = (((begin + offset) % list.length) + list.length) % list.length
        if (matches(this.getOptionText(list[index]).toLowerCase())) return index
      }
      return -1
    }

    const prefixMatch = search(text => text.indexOf(needle) === 0)
    return prefixMatch >= 0 ? prefixMatch : search(text => text.indexOf(needle) >= 0)
  }

  handleTypeAhead (char, list, currentValue) {
    if (this.typeTimer) clearTimeout(this.typeTimer)
    this.typeBuffer += char.toLowerCase()
    this.typeTimer = setTimeout(this.clearTypeBuffer, 800)

    const { isOpen, highlightedIndex } = this.state
    const startIndex = highlightedIndex >= 0 ? highlightedIndex : this.findIndexByValue(list, currentValue)
    const foundIndex = this.findTypeAheadIndex(list, startIndex)

    if (foundIndex < 0) return

    this.setState({ highlightedIndex: foundIndex }, () => this.scrollHighlightedIntoView())

    // Closed box: jump straight to the option, same as a native select.
    if (!isOpen) {
      const { valueProp = 'Value' } = this.props
      this.onChange(list[foundIndex][valueProp])
    }
  }

  moveHighlight (delta, list, currentValue) {
    if (list.length === 0) return

    const { highlightedIndex } = this.state
    const base = highlightedIndex >= 0 ? highlightedIndex : this.findIndexByValue(list, currentValue)
    let next = base < 0 ? (delta > 0 ? 0 : list.length - 1) : base + delta
    next = Math.min(Math.max(next, 0), list.length - 1)

    this.setState({ isOpen: true, highlightedIndex: next }, () => this.scrollHighlightedIntoView())
  }

  handleKeyDown = (e, list, currentValue) => {
    const { isOpen, highlightedIndex } = this.state
    const { valueProp = 'Value' } = this.props

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.moveHighlight(1, list, currentValue)
        return
      case 'ArrowUp':
        e.preventDefault()
        this.moveHighlight(-1, list, currentValue)
        return
      case 'Home':
        if (!isOpen) break
        e.preventDefault()
        this.moveHighlight(-list.length, list, currentValue)
        return
      case 'End':
        if (!isOpen) break
        e.preventDefault()
        this.moveHighlight(list.length, list, currentValue)
        return
      case ' ':
        // Mid-search a space belongs to the term being typed ('akwa ibom'), not to the toggle.
        if (this.typeBuffer.length > 0) break
        // falls through
      case 'Enter':
        e.preventDefault()
        if (isOpen && highlightedIndex >= 0 && list[highlightedIndex]) {
          this.handleOptionClick(list[highlightedIndex][valueProp])
        } else {
          this.toggleDropdown(list, currentValue)
        }
        return
      case 'Escape':
        this.closeDropdown()
        return
      case 'Tab':
        if (isOpen) this.closeDropdown()
        return
    }

    // Type-ahead: any single printable character jumps to the matching option.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault()
      this.handleTypeAhead(e.key, list, currentValue)
    }
  }

  getSelectedText (options, currentValue, showAny) {
    const { valueProp = 'Value', textProp = 'Text' } = this.props

    if (currentValue === 'Any' || currentValue === '' || currentValue === undefined || currentValue === null) {
      if (currentValue === 'Any' || showAny) {
        const foundAny = _.find(options, o => o[valueProp] === 'Any' || o[valueProp] === '')
        if (foundAny) return foundAny[textProp] || 'Any'
        return 'Any'
      }
    }

    const found = _.find(options, o => o[valueProp] == currentValue)
    if (found) {
      return found[textProp] !== undefined ? found[textProp] : found[valueProp]
    }

    return LookupValueFormatter(options, currentValue).Text || currentValue || ''
  }

  getDisplayOptions (options, filterValue, showAny, autosetFirstOption) {
    const { valueProp = 'Value', textProp = 'Text' } = this.props
    const opts = filterValue ? _.filter(options, filterValue) : options

    if (showAny && !autosetFirstOption) {
      const hasAnyInOpts = _.some(opts, o => o[valueProp] === 'Any' || o[valueProp] === '')
      if (!hasAnyInOpts) {
        return _.concat([{ [valueProp]: 'Any', [textProp]: 'Any' }], opts)
      }
    }

    return opts
  }

  renderCustomOptions (list, currentValue) {
    const { valueProp = 'Value' } = this.props
    const { highlightedIndex } = this.state

    this.optionRefs = {}

    return _.map(list, (o, index) => {
      const val = o[valueProp]
      const text = this.getOptionText(o)
      const isSelected = currentValue == val || (val === 'Any' && (currentValue === 'Any' || !currentValue))
      const isHighlighted = index === highlightedIndex

      return (
        <div
          key={`${val}_${index}`}
          ref={el => { this.optionRefs[index] = el }}
          className={`${styleClasses.comboOption || ''} ${isSelected ? styleClasses.isSelected || '' : ''} ${isHighlighted ? styleClasses.isHighlighted || '' : ''}`.trim()}
          onMouseEnter={() => this.setState({ highlightedIndex: index })}
          onClick={() => this.handleOptionClick(val)}
        >
          <span>{text}</span>
          {isSelected && <FAIcon icon="check" style={{ fontSize: '11px' }} />}
        </div>
      )
    })
  }

  render () {
    const { options, readonly: lreadonly, valueProp, textProp, showAny = true, noOptionsText, filterValue, autosetFirstOption, styles, style, className, ...rest } = this.props
    const { optionsAvailable, isOpen } = this.state
    const selectStyle = { ...styles, ...style }
    const comboClassName = `${styleClasses.comboBoxSelect || ''} ${isOpen ? styleClasses.isOpen || '' : ''} ${className || ''}`.trim()

    return (
      <FieldLayoutBox {...rest}>
        {({ showError, value, onChange, name, placeHolder, loading, readonly }) => {
          if (optionsAvailable === true) {
            if (lreadonly || readonly) {
              return <Input type='text' className={comboClassName} style={selectStyle} readOnly name={`static${name}`} id={`static${name}`}
                value={LookupValueFormatter(options, value).Text} />
            } else {
              const displayText = this.getSelectedText(options, value, showAny && !autosetFirstOption)
              const displayOptions = this.getDisplayOptions(options, filterValue, showAny, autosetFirstOption)

              return (
                <div className={styleClasses.comboBoxContainer} ref={this.containerRef}>
                  <div
                    tabIndex={0}
                    role='combobox'
                    aria-expanded={isOpen}
                    className={comboClassName}
                    style={selectStyle}
                    onClick={() => this.toggleDropdown(displayOptions, value)}
                    onKeyDown={(e) => this.handleKeyDown(e, displayOptions, value)}
                  >
                    <span className={styleClasses.selectText}>
                      {displayText || placeHolder || ''}
                    </span>
                    <FAIcon icon={isOpen ? 'chevron-up' : 'chevron-down'} className={`${styleClasses.caretIcon || ''} ${isOpen ? styleClasses.open || '' : ''}`} />
                  </div>

                  {isOpen && (
                    <div className={styleClasses.dropdownMenu}>
                      {this.renderCustomOptions(displayOptions, value)}
                    </div>
                  )}
                </div>
              )
            }
          } else {
            return <Input type='text' className={comboClassName} style={selectStyle} readOnly name={`comboloading${name}`} id={`comboloading${name}`} value={noOptionsText} />
          }
        }}
      </FieldLayoutBox>
    )
  }
}

const ComboBoxClass = compose(
  ReduxWrapper([

  ], [
    ah.formSetFieldValue
  ])
)(ComboBoxClassPlain)

export const ComboBox = ({ parse, format, fieldType, ...rest }) => { return <Field parse={fieldType ? FieldValueParser(fieldType) : parse} format={fieldType ? FieldValueFormatter(fieldType) : format} {...rest} component={ComboBoxClass} /> } // eslint-disable-line
export default ComboBox

